import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Info, Plus, Sparkles, X } from 'lucide-react';
import {
  CALCULATION_DISCLAIMER,
  CALCULATION_MODES,
  CALCULATION_MODE_LABELS,
  SCALE_PRECISION_OPTIONS,
  buildTransferredBlendOutcome,
  calculateSingleStrain,
  decimalsForPrecision,
  estimateSingleStrainFromWeight,
  formatPercentPoints,
  parseCalculatorTransferParams,
  type BlendCalculationRequest,
  type CalculationMode,
  type StrainSummary,
} from '@shared';
import { getProductRepository } from '../lib/productRepository';
import { useRevokeObjectUrls } from '../hooks/useRevokeObjectUrls';
import { revokeDisplayObjectUrls } from '../lib/productImages/objectUrls';
import { blendsApi } from '../lib/blendsApi';
import { StrainSelect } from '../components/StrainSelect';
import { NumberStepper } from '../components/NumberStepper';
import { BlendResultView } from '../components/BlendResultView';
import { SingleStrainResultView } from '../components/SingleStrainResultView';
import { FilterChip } from '../components/ui';
import { CALCULATOR_MAX_STRAINS } from '../lib/calculatorExperience';
import {
  calculatorSessionReducer,
  calculatorUiFlags,
  createInitialCalculatorSession,
  type SingleTargetKind,
} from '../lib/calculatorSession';
import {
  getSingleStrainValidationHint,
  parseStrictPositiveDecimal,
} from '../lib/singleStrainValidation';
import { runBlendCalculationForEpoch } from '../lib/blendCalculationRace';
import { ritualDraftFromProducts } from '../lib/ritualModel';

const WEIGHT_PRESETS = [0.5, 1, 1.25, 1.5, 2] as const;

const SINGLE_STRAIN_MODES: CalculationMode[] = [
  'closest',
  'at_least',
  'do_not_exceed',
];

function summaryBlobUrls(rows: StrainSummary[]): Array<string | null> {
  return rows.flatMap((s) => [
    s.localPrimaryThumbnailUrl,
    s.firstLocalThumbnailUrl,
    s.primaryThumbnailUrl,
  ]);
}

/**
 * Calculator that switches by selected-strain count:
 * 0 → empty setup, 1 → single-strain Target Weight / Target MG, 2+ → blend.
 */
export function Calculator(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [strains, setStrains] = useState<StrainSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const transfer = useMemo(
    () => parseCalculatorTransferParams((k) => params.get(k)),
    [params],
  );
  const initialIds = transfer.strainIds;
  const transferTarget = transfer.target;
  const transferWeight = transfer.weight;
  const transferGramsA = transfer.gramsA;
  const transferGramsB = transfer.gramsB;
  const hasBlendTransfer =
    initialIds.length >= 2 &&
    transferGramsA != null &&
    transferGramsB != null &&
    transferGramsA > 0 &&
    transferGramsB > 0;

  const [session, dispatch] = useReducer(
    calculatorSessionReducer,
    undefined,
    () =>
      createInitialCalculatorSession(initialIds, {
        target: transferTarget,
        weight: transferWeight,
      }),
  );
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const blendTransferHydrated = useRef(false);
  /** Epoch captured at mount for wheel-transfer hydration invalidation. */
  const transferCalculationEpoch = useRef(session.calculationEpoch);
  const transferSnapshot = useRef({
    target: transferTarget ?? String(session.target),
    weight: transferWeight ?? session.weight,
    precision: session.precision,
    gramsA: transferGramsA,
    gramsB: transferGramsB,
  });

  const {
    selectedIds,
    target,
    weight,
    precision,
    mode,
    singleTargetKind,
    rangeMin,
    rangeMax,
    useMaxThc,
    maxThc,
    blendOutcome,
    singleOutcome,
    calcError,
    adding,
    calculationEpoch,
  } = session;

  const ui = calculatorUiFlags(session);
  const { experience } = ui;

  useEffect(() => {
    let cancelled = false;
    getProductRepository()
      .list({ archived: 'active', sort: 'name' })
      .then((rows) => {
        if (cancelled) {
          revokeDisplayObjectUrls(summaryBlobUrls(rows));
          return;
        }
        setStrains(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : 'Could not load strains.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useRevokeObjectUrls(summaryBlobUrls(strains));

  // Hydrate accepted wheel blend allocations once strains are available.
  // Uses the mount-time calculationEpoch so input edits before strains load
  // invalidate the pending transfer (set_blend_outcome epoch mismatch).
  useEffect(() => {
    if (!hasBlendTransfer || blendTransferHydrated.current) return;
    if (selectedIds.length < 2) return;
    if (calculationEpoch !== transferCalculationEpoch.current) {
      blendTransferHydrated.current = true;
      return;
    }
    const primary = strains.find((s) => s.id === selectedIds[0]);
    const balancing = strains.find((s) => s.id === selectedIds[1]);
    if (!primary || !balancing) return;
    const snap = transferSnapshot.current;
    if (snap.gramsA == null || snap.gramsB == null) return;

    const outcome = buildTransferredBlendOutcome({
      primary: {
        id: primary.id,
        name: primary.name ?? primary.productName ?? 'Primary',
        producer: primary.producer ?? undefined,
        thcPercent: primary.thcPercent,
        grams: snap.gramsA,
      },
      balancing: {
        id: balancing.id,
        name: balancing.name ?? balancing.productName ?? 'Balancer',
        producer: balancing.producer ?? undefined,
        thcPercent: balancing.thcPercent,
        grams: snap.gramsB,
      },
      totalWeightGrams: Number(snap.weight),
      targetThcMg: Number(snap.target),
      estimatedThcMg: Number.NaN,
      scalePrecisionGrams: snap.precision,
    });

    blendTransferHydrated.current = true;
    dispatch({
      type: 'set_blend_outcome',
      outcome,
      expectedEpoch: transferCalculationEpoch.current,
    });
  }, [
    hasBlendTransfer,
    selectedIds,
    strains,
    calculationEpoch,
  ]);

  const selectedStrains = useMemo(() => {
    return selectedIds
      .map((id) => strains.find((s) => s.id === id) ?? null)
      .filter((s): s is StrainSummary => s !== null);
  }, [selectedIds, strains]);

  const weightDecimals = decimalsForPrecision(precision);
  const weightNum = Number(weight);
  const activePreset = WEIGHT_PRESETS.find(
    (p) => Math.abs(p - weightNum) < 1e-6,
  );

  const singleValidationHint = useMemo((): string | null => {
    if (experience !== 'single') return null;
    const strain = selectedStrains[0];
    return getSingleStrainValidationHint({
      kind: singleTargetKind,
      weightText: weight,
      targetText: target,
      thcPercent: strain?.thcPercent,
      hasStrain: Boolean(strain),
    });
  }, [
    experience,
    selectedStrains,
    singleTargetKind,
    weight,
    target,
  ]);

  const blendValidationHint = useMemo((): string | null => {
    if (experience !== 'blend') return null;
    if (selectedStrains.length < 2) {
      return 'Pick at least two different strains to blend.';
    }
    const unique = new Set(selectedStrains.map((s) => s.id));
    if (unique.size !== selectedStrains.length) {
      return 'Same strain twice won’t make a blend — choose different products.';
    }
    if (!Number.isFinite(weightNum) || weightNum <= 0) {
      return 'Joint size needs to be a positive weight in grams.';
    }
    const t = Number(target);
    if (!Number.isFinite(t) || t <= 0) {
      return 'Target THC should be a positive number in milligrams.';
    }
    return null;
  }, [experience, selectedStrains, weightNum, target]);

  const blendRequest = useMemo<BlendCalculationRequest | null>(() => {
    if (experience !== 'blend' || blendValidationHint) return null;
    const w = Number(weight);
    const t = Number(target);
    if (!Number.isFinite(w) || !Number.isFinite(t) || w <= 0 || t <= 0) {
      return null;
    }
    return {
      strains: selectedStrains.map((s, i) => ({
        strainId: s.id,
        name: s.name ?? s.productName ?? `Strain ${i + 1}`,
        producer: s.producer ?? undefined,
        thcPercent: s.thcPercent,
      })),
      totalWeightGrams: w,
      targetThcMg: t,
      scalePrecisionGrams: precision,
      mode,
      ...(mode === 'target_range'
        ? { rangeMinMg: Number(rangeMin), rangeMaxMg: Number(rangeMax) }
        : {}),
      ...(useMaxThc ? { maxThcMg: Number(maxThc) } : {}),
    };
  }, [
    experience,
    blendValidationHint,
    selectedStrains,
    weight,
    target,
    precision,
    mode,
    rangeMin,
    rangeMax,
    useMaxThc,
    maxThc,
  ]);

  const calculateSingle = (): void => {
    if (experience !== 'single') return;
    const strain = selectedStrains[0];
    if (!strain) {
      dispatch({
        type: 'set_calc_error',
        message: 'Select a strain first.',
      });
      return;
    }
    if (singleValidationHint) {
      dispatch({ type: 'set_calc_error', message: singleValidationHint });
      return;
    }

    const startedEpoch = sessionRef.current.calculationEpoch;
    const strainName = strain.name ?? strain.productName ?? 'Selected strain';
    dispatch({
      type: 'set_calc_error',
      message: null,
      expectedEpoch: startedEpoch,
    });

    if (singleTargetKind === 'target_weight') {
      const parsedWeight = parseStrictPositiveDecimal(weight);
      if (!parsedWeight.ok) {
        dispatch({
          type: 'set_calc_error',
          message: singleValidationHint ?? 'Invalid joint weight.',
          expectedEpoch: startedEpoch,
        });
        return;
      }
      const outcome = estimateSingleStrainFromWeight({
        strainName,
        thcPercent: strain.thcPercent,
        weightGrams: parsedWeight.value,
        scalePrecisionGrams: precision,
      });
      dispatch({
        type: 'set_single_outcome',
        outcome: { kind: 'target_weight', outcome },
        expectedEpoch: startedEpoch,
      });
      return;
    }

    const parsedTarget = parseStrictPositiveDecimal(target);
    if (!parsedTarget.ok) {
      dispatch({
        type: 'set_calc_error',
        message: singleValidationHint ?? 'Invalid target THC.',
        expectedEpoch: startedEpoch,
      });
      return;
    }
    const roundingMode = mode === 'target_range' ? 'closest' : mode;
    const outcome = calculateSingleStrain({
      strainName,
      thcPercent: strain.thcPercent,
      targetThcMg: parsedTarget.value,
      scalePrecisionGrams: precision,
      mode: roundingMode,
    });
    dispatch({
      type: 'set_single_outcome',
      outcome: { kind: 'target_mg', outcome },
      expectedEpoch: startedEpoch,
    });
  };

  const setSingleTargetKind = (value: SingleTargetKind): void => {
    dispatch({ type: 'set_single_target_kind', value });
  };

  const calculateBlend = async (): Promise<void> => {
    if (blendValidationHint) {
      dispatch({ type: 'set_calc_error', message: blendValidationHint });
      return;
    }
    if (!blendRequest) {
      dispatch({
        type: 'set_calc_error',
        message: 'Select at least two strains first.',
      });
      return;
    }
    const startedEpoch = sessionRef.current.calculationEpoch;
    const request = blendRequest;
    setBusy(true);
    dispatch({
      type: 'set_calc_error',
      message: null,
      expectedEpoch: startedEpoch,
    });
    dispatch({ type: 'set_save_msg', message: null });
    try {
      const settled = await runBlendCalculationForEpoch({
        startedEpoch,
        getCurrentEpoch: () => sessionRef.current.calculationEpoch,
        calculate: () => blendsApi.calculate(request),
      });
      if (settled.status === 'accepted') {
        dispatch({
          type: 'set_blend_outcome',
          outcome: settled.outcome,
          expectedEpoch: settled.epoch,
        });
      } else if (settled.status === 'error' && !settled.stale) {
        dispatch({
          type: 'set_calc_error',
          message: settled.message,
          expectedEpoch: settled.epoch,
        });
        dispatch({
          type: 'set_blend_outcome',
          outcome: null,
          expectedEpoch: settled.epoch,
        });
      }
      // stale success/error: ignore — calculationEpoch already advanced
    } finally {
      setBusy(false);
    }
  };

  const modeOptions =
    experience === 'single' ? SINGLE_STRAIN_MODES : [...CALCULATION_MODES];

  const pageLead =
    experience === 'blend'
      ? 'Two or more strains, one joint size, a target THC.'
      : experience === 'single'
        ? 'One strain — calculate from a joint weight or a THC target.'
        : 'Start with one strain. Add another anytime to blend.';

  const singleStrain = selectedStrains[0];
  const singleWeightResult = singleOutcome?.kind === 'target_weight' && singleOutcome.outcome.ok
    ? singleOutcome.outcome
    : null;
  const singleDoseResult = singleOutcome?.kind === 'target_mg' && singleOutcome.outcome.ok
    ? singleOutcome.outcome
    : null;

  return (
    <div className="stack calc-page">
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Calculator</h1>
          <p className="page-lead">{pageLead}</p>
          <Link to="/dose" className="calculator-utility-link">
            Open Dose Calculator
          </Link>
        </div>
        <span
          className="icon-btn"
          title={CALCULATION_DISCLAIMER}
          aria-label="About calculation estimates"
        >
          <Info size={18} strokeWidth={2} />
        </span>
      </div>

      {loadError && (
        <div className="notice" role="alert">
          {loadError}
        </div>
      )}

      <section className="card stack calc-step">
        <h2 className="calc-step__title">1 · Choose strains</h2>

        {ui.showEmptySetup ? (
          <StrainSelect
            label="Strain"
            strains={strains}
            selectedId={null}
            onSelect={(s) =>
              dispatch({ type: 'select_first', strainId: s.id })
            }
          />
        ) : (
          <div className="stack" style={{ gap: '0.75rem' }}>
            {selectedIds.map((id, index) => {
              const strain = strains.find((s) => s.id === id);
              const excludeIds = selectedIds.filter((other) => other !== id);
              return (
                <div
                  key={`${id}-${index}`}
                  className="stack"
                  style={{ gap: '0.4rem' }}
                >
                  <div className="spread" style={{ alignItems: 'center' }}>
                    <span className="muted" style={{ fontSize: '0.85rem' }}>
                      Strain {index + 1}
                      {strain
                        ? ` · ${formatPercentPoints(strain.thcPercent)} THC`
                        : ''}
                    </span>
                    <button
                      type="button"
                      className="btn"
                      style={{ minHeight: '36px', padding: '0.3rem 0.6rem' }}
                      onClick={() =>
                        dispatch({ type: 'remove_strain', strainId: id })
                      }
                      aria-label={`Remove strain ${index + 1}`}
                    >
                      <X size={16} strokeWidth={2} aria-hidden />
                      Remove
                    </button>
                  </div>
                  <StrainSelect
                    label={`Strain ${index + 1}`}
                    strains={strains}
                    selectedId={id}
                    excludeIds={excludeIds}
                    onSelect={(s) =>
                      dispatch({
                        type: 'replace_strain',
                        index,
                        strainId: s.id,
                      })
                    }
                  />
                </div>
              );
            })}

            {adding ? (
              <StrainSelect
                label={`Strain ${selectedIds.length + 1}`}
                strains={strains}
                selectedId={null}
                excludeIds={selectedIds}
                onSelect={(s) =>
                  dispatch({ type: 'add_strain', strainId: s.id })
                }
              />
            ) : null}

            {ui.showAddStrainControl ? (
              <button
                type="button"
                className="btn"
                onClick={() => dispatch({ type: 'begin_add' })}
              >
                <Plus size={16} strokeWidth={2} aria-hidden />
                Add another strain
              </button>
            ) : null}

            {selectedIds.length >= CALCULATOR_MAX_STRAINS ? (
              <p className="hint">
                Up to {CALCULATOR_MAX_STRAINS} strains can be blended here.
              </p>
            ) : null}
          </div>
        )}
      </section>

      {ui.showSingleCalculator ? (
        <form
          className="stack"
          style={{ gap: 'inherit' }}
          onSubmit={(event) => {
            event.preventDefault();
            calculateSingle();
          }}
        >
          <section className="card stack calc-step">
            <h2 className="calc-step__title">2 · What do you want to set?</h2>
            <div
              className="segmented"
              role="radiogroup"
              aria-label="Single-strain calculation mode"
            >
              <button
                type="button"
                role="radio"
                aria-checked={singleTargetKind === 'target_weight'}
                className={
                  singleTargetKind === 'target_weight' ? 'is-active' : ''
                }
                onClick={() => setSingleTargetKind('target_weight')}
              >
                Target Weight
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={singleTargetKind === 'target_mg'}
                className={singleTargetKind === 'target_mg' ? 'is-active' : ''}
                onClick={() => setSingleTargetKind('target_mg')}
              >
                Target MG
              </button>
            </div>
            <p className="hint muted">
              {singleTargetKind === 'target_weight'
                ? 'Enter a joint weight to see how much THC it contains.'
                : 'Enter a THC target to see how much of this strain you need.'}
            </p>
          </section>

          {singleTargetKind === 'target_weight' ? (
            <section className="card stack calc-step">
              <h2 className="calc-step__title">3 · Joint weight</h2>
              <NumberStepper
                label="Joint weight"
                value={weight}
                onChange={(v) => dispatch({ type: 'set_weight', value: v })}
                step={precision}
                min={precision}
                suffix="g"
                decimals={weightDecimals}
                hint={`Scale step ${precision} g`}
              />
              <AdvancedOptions
                showAdvanced={showAdvanced}
                setShowAdvanced={setShowAdvanced}
                precision={precision}
                setPrecision={(n) =>
                  dispatch({ type: 'set_precision', value: n })
                }
                mode={mode}
                setMode={(m) => dispatch({ type: 'set_mode', value: m })}
                modeOptions={modeOptions}
                showMode={false}
                showRange={false}
                rangeMin={rangeMin}
                setRangeMin={(v) =>
                  dispatch({ type: 'set_range_min', value: v })
                }
                rangeMax={rangeMax}
                setRangeMax={(v) =>
                  dispatch({ type: 'set_range_max', value: v })
                }
                useMaxThc={false}
                setUseMaxThc={(v) =>
                  dispatch({ type: 'set_use_max_thc', value: v })
                }
                maxThc={maxThc}
                setMaxThc={(v) => dispatch({ type: 'set_max_thc', value: v })}
              />
            </section>
          ) : (
            <section className="card stack calc-step">
              <h2 className="calc-step__title">3 · Target THC</h2>
              <NumberStepper
                label="Target THC"
                value={target}
                onChange={(v) => dispatch({ type: 'set_target', value: v })}
                step={1}
                min={1}
                suffix="mg"
                decimals={0}
              />
              <label className="calc-slider-wrap">
                <span className="sr-only">Target THC slider</span>
                <input
                  type="range"
                  min={10}
                  max={120}
                  step={1}
                  value={Math.min(120, Math.max(10, Number(target) || 10))}
                  onChange={(e) =>
                    dispatch({ type: 'set_target', value: e.target.value })
                  }
                />
                <span className="calc-slider-value" aria-live="polite">
                  {Number(target) || 0} mg
                </span>
              </label>

              <AdvancedOptions
                showAdvanced={showAdvanced}
                setShowAdvanced={setShowAdvanced}
                precision={precision}
                setPrecision={(n) =>
                  dispatch({ type: 'set_precision', value: n })
                }
                mode={mode}
                setMode={(m) => dispatch({ type: 'set_mode', value: m })}
                modeOptions={modeOptions}
                showMode
                showRange={false}
                rangeMin={rangeMin}
                setRangeMin={(v) =>
                  dispatch({ type: 'set_range_min', value: v })
                }
                rangeMax={rangeMax}
                setRangeMax={(v) =>
                  dispatch({ type: 'set_range_max', value: v })
                }
                useMaxThc={false}
                setUseMaxThc={(v) =>
                  dispatch({ type: 'set_use_max_thc', value: v })
                }
                maxThc={maxThc}
                setMaxThc={(v) => dispatch({ type: 'set_max_thc', value: v })}
              />
            </section>
          )}

          <section className="card stack calc-step">
            <button
              type="submit"
              className="btn btn-lg btn-blend btn-block"
              disabled={Boolean(singleValidationHint)}
            >
              <Sparkles size={18} strokeWidth={2} aria-hidden />
              Calculate
            </button>
            {singleValidationHint && !calcError ? (
              <p className="hint">{singleValidationHint}</p>
            ) : null}
            {calcError && experience === 'single' ? (
              <div className="notice" role="alert">
                {calcError}
              </div>
            ) : null}
          </section>

          {ui.showSingleResult && singleOutcome?.kind === 'target_weight' ? (
            <SingleStrainResultView
              kind="target_weight"
              outcome={singleOutcome.outcome}
              brand={singleStrain?.brand}
              onSaveAsRitual={singleStrain && singleWeightResult ? () => navigate('/rituals', { state: { ritualDraft: ritualDraftFromProducts([{ product: singleStrain, weightGrams: singleWeightResult.weightGrams, thcDoseMg: singleWeightResult.estimatedThcMg }], 'weight', 'blend-calculator') } }) : undefined}
            />
          ) : null}
          {ui.showSingleResult && singleOutcome?.kind === 'target_mg' ? (
            <SingleStrainResultView
              kind="target_mg"
              outcome={singleOutcome.outcome}
              brand={singleStrain?.brand}
              onSaveAsRitual={singleStrain && singleDoseResult ? () => navigate('/rituals', { state: { ritualDraft: ritualDraftFromProducts([{ product: singleStrain, weightGrams: singleDoseResult.recommendedWeightGrams, thcDoseMg: singleDoseResult.estimatedThcMg }], 'dose', 'blend-calculator') } }) : undefined}
            />
          ) : null}
        </form>
      ) : null}

      {ui.showBlendCalculator ? (
        <>
          <section className="card stack calc-step">
            <h2 className="calc-step__title">2 · Joint size</h2>
            <div
              className="filter-chips"
              role="group"
              aria-label="Joint size presets"
            >
              {WEIGHT_PRESETS.map((p) => (
                <FilterChip
                  key={p}
                  label={`${p} g`}
                  active={activePreset === p}
                  onClick={() =>
                    dispatch({
                      type: 'set_weight',
                      value: p.toFixed(weightDecimals),
                    })
                  }
                />
              ))}
            </div>
            <NumberStepper
              label="Custom weight"
              value={weight}
              onChange={(v) => dispatch({ type: 'set_weight', value: v })}
              step={precision}
              suffix="g"
              decimals={weightDecimals}
              hint={`Scale step ${precision} g`}
            />
          </section>

          <section className="card stack calc-step">
            <h2 className="calc-step__title">3 · Target THC</h2>
            <NumberStepper
              label="Total THC"
              value={target}
              onChange={(v) => dispatch({ type: 'set_target', value: v })}
              step={1}
              min={1}
              suffix="mg"
              decimals={0}
            />
            <label className="calc-slider-wrap">
              <span className="sr-only">Target THC slider</span>
              <input
                type="range"
                min={10}
                max={120}
                step={1}
                value={Math.min(120, Math.max(10, Number(target) || 10))}
                onChange={(e) =>
                  dispatch({ type: 'set_target', value: e.target.value })
                }
              />
              <span className="calc-slider-value" aria-live="polite">
                {Number(target) || 0} mg
              </span>
            </label>

            <AdvancedOptions
              showAdvanced={showAdvanced}
              setShowAdvanced={setShowAdvanced}
              precision={precision}
              setPrecision={(n) =>
                dispatch({ type: 'set_precision', value: n })
              }
              mode={mode}
              setMode={(m) => dispatch({ type: 'set_mode', value: m })}
              modeOptions={modeOptions}
              showRange={mode === 'target_range'}
              rangeMin={rangeMin}
              setRangeMin={(v) =>
                dispatch({ type: 'set_range_min', value: v })
              }
              rangeMax={rangeMax}
              setRangeMax={(v) =>
                dispatch({ type: 'set_range_max', value: v })
              }
              useMaxThc={useMaxThc}
              setUseMaxThc={(v) =>
                dispatch({ type: 'set_use_max_thc', value: v })
              }
              maxThc={maxThc}
              setMaxThc={(v) => dispatch({ type: 'set_max_thc', value: v })}
            />

            <button
              type="button"
              className="btn btn-lg btn-blend btn-block"
              onClick={() => void calculateBlend()}
              disabled={busy || Boolean(blendValidationHint)}
            >
              <Sparkles size={18} strokeWidth={2} aria-hidden />
              {busy ? 'Calculating…' : 'Calculate Blend'}
            </button>

            {blendValidationHint && !calcError ? (
              <p className="hint">{blendValidationHint}</p>
            ) : null}

            {calcError && (
              <div className="notice" role="alert">
                {calcError}
              </div>
            )}
          </section>

          {ui.showBlendResult && blendOutcome ? (
            <BlendResultView
              outcome={blendOutcome}
              onSaveAsRitual={blendOutcome.result ? () => navigate('/rituals', { state: { ritualDraft: ritualDraftFromProducts(blendOutcome.result!.allocations.map((allocation) => ({ product: selectedStrains[allocation.strainIndex]!, weightGrams: allocation.grams, thcDoseMg: allocation.thcMg })), 'weight', 'blend-calculator') } }) : undefined}
              onNewCalculation={() =>
                dispatch({ type: 'reset_blend_output' })
              }
            />
          ) : null}
        </>
      ) : null}

      <p className="notice" style={{ borderLeftColor: 'var(--color-primary)' }}>
        {CALCULATION_DISCLAIMER}
      </p>
    </div>
  );
}

function AdvancedOptions(props: {
  showAdvanced: boolean;
  setShowAdvanced: (fn: (v: boolean) => boolean) => void;
  precision: number;
  setPrecision: (n: number) => void;
  mode: CalculationMode;
  setMode: (m: CalculationMode) => void;
  modeOptions: CalculationMode[];
  /** When false, hide rounding mode (unused by Target Weight). Default true. */
  showMode?: boolean;
  showRange: boolean;
  rangeMin: string;
  setRangeMin: (v: string) => void;
  rangeMax: string;
  setRangeMax: (v: string) => void;
  useMaxThc: boolean;
  setUseMaxThc: (v: boolean) => void;
  maxThc: string;
  setMaxThc: (v: string) => void;
}): JSX.Element {
  const {
    showAdvanced,
    setShowAdvanced,
    precision,
    setPrecision,
    mode,
    setMode,
    modeOptions,
    showMode = true,
    showRange,
    rangeMin,
    setRangeMin,
    rangeMax,
    setRangeMax,
    useMaxThc,
    setUseMaxThc,
    maxThc,
    setMaxThc,
  } = props;

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => setShowAdvanced((v) => !v)}
        aria-expanded={showAdvanced}
      >
        {showAdvanced ? 'Hide advanced' : 'Advanced options'}
      </button>

      {showAdvanced ? (
        <div className="stack" style={{ gap: '0.75rem' }}>
          <div className={showMode ? 'grid grid-2' : undefined}>
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor="precision">Scale precision</label>
              <select
                id="precision"
                value={precision}
                onChange={(e) => setPrecision(Number(e.target.value))}
              >
                {SCALE_PRECISION_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p} g
                  </option>
                ))}
              </select>
            </div>
            {showMode ? (
              <div className="field" style={{ margin: 0 }}>
                <label htmlFor="mode">Mode</label>
                <select
                  id="mode"
                  value={modeOptions.includes(mode) ? mode : modeOptions[0]}
                  onChange={(e) => setMode(e.target.value as CalculationMode)}
                >
                  {modeOptions.map((m) => (
                    <option key={m} value={m}>
                      {CALCULATION_MODE_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          {showRange && (
            <div className="grid grid-2">
              <NumberStepper
                label="Range minimum"
                value={rangeMin}
                onChange={setRangeMin}
                step={5}
                suffix="mg"
                decimals={0}
              />
              <NumberStepper
                label="Range maximum"
                value={rangeMax}
                onChange={setRangeMax}
                step={5}
                suffix="mg"
                decimals={0}
              />
            </div>
          )}
          {modeOptions.includes('target_range') ? (
            <div className="field" style={{ margin: 0 }}>
              <label className="row" style={{ gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={useMaxThc}
                  onChange={(e) => setUseMaxThc(e.target.checked)}
                  style={{ width: 'auto', minHeight: 'auto' }}
                />
                <span>Apply a maximum THC ceiling</span>
              </label>
              {useMaxThc ? (
                <NumberStepper
                  label="Maximum THC"
                  value={maxThc}
                  onChange={setMaxThc}
                  step={5}
                  suffix="mg"
                  decimals={0}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
